"use client";

/**
 * HeroSequence — replacement for the mp4 hero on the Svendetid landing page.
 *
 * Why: the old /hero-video.mp4 rendered its copy as tiny pixels inside a
 * 2560x1440 frame that got letterboxed into the hero, so the headline and
 * bullets were unreadable in production. This rebuilds the same 10s sequence
 * as live DOM — real text at real sizes, sharp at any DPR, ~6 KB of markup
 * instead of a multi-MB video — and lets the phone's output physically fly
 * out of the screen and become the page's own bullet copy.
 *
 * Sequence
 *   0.1s  hand + phone rise into frame, screen dark
 *   0.35s pre-headline ("Træt af papirarbejdet efter fyraften?") fades in
 *   0.85s screen wakes
 *   2.05s pre-headline resolves upward into the mono eyebrow
 *   2.45s H1 + trade rotator, subhead
 *   3.5s  three result cards eject from the phone screen and land as the
 *         page's bullet rows, shedding their card chrome on arrival
 *   5.9s  CTA
 *   6.2s  hairline beam draws down into the next section
 *   loop  in-phone chat cycles (15s) + ghost cards keep flying out;
 *         everything pauses when the hero scrolls out of view
 *
 * Requirements
 *   - /public/hand-phone.png  (715x974, transparent background + screen cut-out)
 *   - /public/logo.png       (the design system's sunrise mark, used as Svend's avatar)
 *   - Fonts: Sora, Newsreader, DM Sans, DM Mono (already imported by the page)
 *   - No icon dependency: the five Ionicons glyphs used are inlined below.
 */

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";

/* ---------------------------------------------------------------- geometry --
 * Measured from hand-phone.png (960x1440 - scaled 2x). The screen is a transparent
 * cut-out, so the live UI sits *behind* the PNG and the fingers overlap it
 * for free. Keep these in sync if the photo is ever re-cropped.
 * -------------------------------------------------------------------------- */
const ART_W = 960;
const ART_H = 1440;
const SCREEN = { left: 310, top: 250, width: 374, height: 810, radius: 40 };
/* The app UI is authored at a comfortable 528x1150 and scaled into the photo's
   smaller screen rect, so every inner value stays legible in source. */
const UI = { width: 528, height: 1150, scale: SCREEN.width / 528 };

const EASE = "cubic-bezier(0.16,1,0.3,1)";
/* The hero clips the last ~56 scaled px of the arm; fade it out rather than
   letting a razor edge cut across the palm. */
const WRIST_FADE = "linear-gradient(to bottom, #000 88%, rgba(0,0,0,0) 99%)";

const EJECT_DELAY = [3.5, 4.15, 4.8];

/* Each chat pane arrives because the question was just typed into the input. The
   reel's stops sit at 3.6 / 7.35 / 11.1s inside its 15s loop, so the question for
   the next pane is written in the 1.65s before its stop. The 4th stop has no
   typing: that pane repeats the 1st so the loop reset stays invisible. */
const TYPE_STOPS = [3600, 7350, 11100];
const TYPE_LEAD = 1900, TYPE_GAP = 250, TYPE_OFFSET = 1600;

type Lang = "da" | "en";

const COPY = {
  da: {
    eyebrow: "Træt af papirarbejdet?",
    preHead: ["Træt af papirarbejdet", "efter fyraften?"],
    preSub: "Slip det. Svend tager over — i lommen.",
    h1: "Alt på ét sted for",
    trades: ["Tømrere", "Elektrikere", "VVS-montører", "Serviceteknikere", "Malere", "Murere"],
    sub: "Svendetid samler tilbud, fakturering, timeregistrering og kundekartotek i én app — så aftenen bliver din igen.",
    cta: "Start gratis prøveperiode →",
    ctaNote: "Ingen binding · 14 dage gratis",
    cue: "Se hvordan Svend arbejder",
    assistant: "AI-assistent",
    online: "Online",
    input: "Spørg Svend",
    alt: "Håndværker der bruger Svendetid på mobilen",
    bullets: [
      { bold: "Uendelig produktivitet.", text: "AI-agenter og automatiske workflows.", href: "/features/automation" },
      { bold: "Fuldt overblik.", text: "Indbygget kalender med alle jobs og aftaler samlet ét sted.", href: "/features/calendar" },
      { bold: "Nem tidsregistrering.", text: "Registrér arbejdstimer, ferie og fravær direkte i appen.", href: "/features/time-registration" },
    ],
    cueHref: "/features",
    chat: [
      { q: "Lav en faktura på Bredgade 8", a: "Fandt 7,5 timer og 3 materialer. Klar til at sende.", label: "Faktura · 1042", chip: "Klar", value: "kr 12.480", meta: "Nyt badeværelse · Anna Jensen" },
      { q: "Hvad er min omsætning i juli?", a: "Op 18 % mod juni. Bedste måned i år.", label: "Omsætning · Juli", chip: "+18 %", value: "kr 248.300", meta: "14 afsluttede jobs · 6 kunder" },
      { q: "Book service hos Møllevej 3", a: "Lagt i kalenderen tirsdag 13–16.", label: "Kalender · Tirsdag", chip: "Booket", value: "13–16", meta: "Service, Møllevej 3 · Lars Kjær" },
    ],
  },
  en: {
    eyebrow: "Tired of the paperwork?",
    preHead: ["Tired of the paperwork", "after hours?"],
    preSub: "Let it go. Svend takes over — from your pocket.",
    h1: "Everything in one place for",
    trades: ["Carpenters", "Electricians", "Plumbers", "Service technicians", "Painters", "Bricklayers"],
    sub: "Svendetid brings quotes, invoicing, time tracking and your customer database into one app — so your evenings are yours again.",
    cta: "Get started. It's FREE!",
    ctaNote: "No commitment · 14 days free",
    cue: "See how Svend works",
    assistant: "AI assistant",
    online: "Online",
    input: "Ask Svend",
    alt: "A tradesperson using Svendetid on mobile",
    bullets: [
      { bold: "Create infinite productivity.", text: "AI agents and automated workflows.", href: "/en/features/automation" },
      { bold: "Full overview.", text: "Built-in calendar with every job and appointment in one place.", href: "/en/features/calendar" },
      { bold: "Easy time tracking.", text: "Log work hours, vacation and absence right in the app.", href: "/en/features/time-registration" },
    ],
    cueHref: "/en/features",
    chat: [
      { q: "Draft an invoice for Bredgade 8", a: "Found 7.5 hours and 3 materials. Ready to send.", label: "Invoice · 1042", chip: "Ready", value: "kr 12,480", meta: "New bathroom · Anna Jensen" },
      { q: "What's my revenue in July?", a: "Up 18% on June. Best month this year.", label: "Revenue · July", chip: "+18%", value: "kr 248,300", meta: "14 jobs completed · 6 customers" },
      { q: "Book a service at Møllevej 3", a: "Added to the calendar, Tuesday 13–16.", label: "Calendar · Tuesday", chip: "Booked", value: "13–16", meta: "Service, Møllevej 3 · Lars Kjær" },
    ],
  },
} as const;

const BULLET_STYLE = [
  { icon: "sparkles-outline", tint: "#FFEFDD", ink: "#C2410C" },
  { icon: "calendar-outline", tint: "#E0ECE7", ink: "#2F6B48" },
  { icon: "time-outline", tint: "#E4ECFA", ink: "#2056C0" },
];

/* ------------------------------------------------------------------- icons --
 * The real Ionicons 7.1.0 glyphs, inlined. The design system bakes its icon set
 * in for exactly this reason: the <ion-icon> web component fetches each glyph
 * from a CDN at runtime and leaves the element visibility:hidden until that
 * resolves, so a blocked or slow fetch renders empty circles.
 * -------------------------------------------------------------------------- */
const ICONS: Record<string, { vb: string; inner: string }> = {
  "sparkles-outline": { vb: "0 0 512 512", inner: '<path d="M259.92 262.91L216.4 149.77a9 9 0 00-16.8 0l-43.52 113.14a9 9 0 01-5.17 5.17L37.77 311.6a9 9 0 000 16.8l113.14 43.52a9 9 0 015.17 5.17l43.52 113.14a9 9 0 0016.8 0l43.52-113.14a9 9 0 015.17-5.17l113.14-43.52a9 9 0 000-16.8l-113.14-43.52a9 9 0 01-5.17-5.17zM108 68L88 16 68 68 16 88l52 20 20 52 20-52 52-20-52-20zM426.67 117.33L400 48l-26.67 69.33L304 144l69.33 26.67L400 240l26.67-69.33L496 144l-69.33-26.67z" stroke-linecap="round" stroke-linejoin="round" class="ionicon-fill-none ionicon-stroke-width"/>' },
  "calendar-outline": { vb: "0 0 512 512", inner: '<rect stroke-linejoin="round" x="48" y="80" width="416" height="384" rx="48" class="ionicon-fill-none ionicon-stroke-width"/><circle cx="296" cy="232" r="24"/><circle cx="376" cy="232" r="24"/><circle cx="296" cy="312" r="24"/><circle cx="376" cy="312" r="24"/><circle cx="136" cy="312" r="24"/><circle cx="216" cy="312" r="24"/><circle cx="136" cy="392" r="24"/><circle cx="216" cy="392" r="24"/><circle cx="296" cy="392" r="24"/><path stroke-linejoin="round" stroke-linecap="round" d="M128 48v32M384 48v32" class="ionicon-fill-none ionicon-stroke-width"/><path stroke-linejoin="round" d="M464 160H48" class="ionicon-fill-none ionicon-stroke-width"/>' },
  "time-outline": { vb: "0 0 512 512", inner: '<path d="M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64z" stroke-miterlimit="10" class="ionicon-fill-none ionicon-stroke-width"/><path stroke-linecap="round" stroke-linejoin="round" d="M256 128v144h96" class="ionicon-fill-none ionicon-stroke-width"/>' },
  "arrow-forward": { vb: "0 0 512 512", inner: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M268 112l144 144-144 144M392 256H100" class="ionicon-fill-none"/>' },
  "arrow-up": { vb: "0 0 512 512", inner: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="48" d="M112 244l144-144 144 144M256 120v292" class="ionicon-fill-none"/>' },
};

function Icon({ name, size }: { name: string; size: number }) {
  const g = ICONS[name];
  if (!g) return null;
  return (
    <span className="svd-ion" style={{ display: "inline-flex", lineHeight: 0, fontSize: size, flex: "none" }}>
      <svg viewBox={g.vb} width="1em" height="1em" aria-hidden="true" dangerouslySetInnerHTML={{ __html: g.inner }} />
    </span>
  );
}

export default function HeroSequence({ lang = "da" }: { lang?: Lang }) {
  const t = COPY[lang];

  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const beamRef = useRef<HTMLDivElement>(null);
  const tradeRef = useRef<HTMLSpanElement>(null);
  const typeRef = useRef<HTMLSpanElement>(null);
  const phRef = useRef<HTMLSpanElement>(null);
  const caretRef = useRef<HTMLSpanElement>(null);

  const [trade, setTrade] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTrade((p) => (p + 1) % t.trades.length), 2000);
    return () => clearInterval(id);
  }, [t.trades.length]);

  /* Written straight to the DOM each frame: no setState, so the typing can't
     churn re-renders or reset the imperative eject/ghost timelines. */
  useEffect(() => {
    const t0 = performance.now();
    let raf = 0;
    let last: string | null = null;
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const el = typeRef.current;
      if (!el) return;
      const s = typedAt(performance.now() - t0);
      if (s === last) return;
      last = s;
      el.textContent = s;
      if (phRef.current) phRef.current.style.opacity = s ? "0" : "1";
      if (caretRef.current) caretRef.current.style.opacity = s ? "1" : "0";
    };
    frame();
    return () => cancelAnimationFrame(raf);
  }, []);

  // Replay the rotator's entrance on every change.
  useEffect(() => {
    const el = tradeRef.current;
    if (!el) return;
    el.style.animation = "none";
    void el.offsetWidth;
    el.style.animation = `svTradeIn 0.6s ${EASE} both`;
  }, [trade]);

  /* Scale the artwork to the hero, then measure how far each bullet sits from
     the phone screen so the ejected cards can start *inside* the screen. */
  useEffect(() => {
    const fit = () => {
      const stage = stageRef.current;
      const group = groupRef.current;
      const root = rootRef.current;
      const screen = screenRef.current;
      if (!stage || !group || !root || !screen) return;

      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (!w || !h) return;

      // Height-led, with a modest bleed off the right edge.
      const s = Math.min((h + 56) / ART_H, (w * 1.25) / ART_W, 0.9);
      group.style.transform = `scale(${s})`;
      // Any overflow goes RIGHT so the photograph never slides under the copy.
      group.style.marginLeft = `${-ART_W / 2 + Math.max(0, (ART_W * s - w) / 2)}px`;

      const sr = screen.getBoundingClientRect();
      const ox = sr.left + sr.width / 2;
      const oy = sr.top + sr.height * 0.42;
      root.querySelectorAll<HTMLElement>("[data-fly]").forEach((el) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty("--bx", `${ox - (r.left + r.width / 2)}px`);
        el.style.setProperty("--by", `${oy - (r.top + r.height / 2)}px`);
      });

      if (beamRef.current) {
        beamRef.current.style.left = `${ox - root.getBoundingClientRect().left}px`;
      }
    };

    fit();
    const late = setTimeout(fit, 600);
    window.addEventListener("resize", fit);
    if (document.fonts?.ready) void document.fonts.ready.then(fit);

    // Per-index timelines are attached here rather than inline so the markup
    // stays static and server-renderable. Delays are measured from mount and
    // each element is armed once, so a row re-created mid-flight (the trade
    // rotator re-renders every 2s) resumes instead of restarting at 0%.
    const root = rootRef.current;
    const t0 = performance.now();
    const arm = (el: HTMLElement, css: string) => {
      if (el.dataset.armed === "1") return;
      el.dataset.armed = "1";
      el.style.animation = css;
    };
    const armAll = () => {
      const elapsed = (performance.now() - t0) / 1000;
      root?.querySelectorAll<HTMLElement>("[data-fly]").forEach((el, i) => {
        arm(el, `svEject 1.25s ${EASE} ${EJECT_DELAY[i % 3] - elapsed}s both`);
      });
    };
    armAll();
    const armLate = setInterval(armAll, 2000);

    // Loop only while the hero is on screen.
    let io: IntersectionObserver | undefined;
    if (root && typeof IntersectionObserver !== "undefined") {
      const setPlay = (v: "running" | "paused") =>
        root.querySelectorAll<HTMLElement>("*").forEach((el) => {
          el.style.animationPlayState = v;
        });
      io = new IntersectionObserver(([e]) => setPlay(e.isIntersecting ? "running" : "paused"), { threshold: 0 });
      io.observe(root);
    }

    return () => {
      clearTimeout(late);
      clearInterval(armLate);
      window.removeEventListener("resize", fit);
      io?.disconnect();
    };
  }, []);

  // The 4th exchange repeats the 1st so the loop's reset is invisible.
  const chat = [...t.chat, t.chat[0]];

  /* What the input shows at `ms` into the reel's loop. Pure, so the timing can
     be reasoned about without waiting on the clock. */
  const typedAt = (ms: number) => {
    const p = (((ms - TYPE_OFFSET) % 15000) + 15000) % 15000;
    for (let i = 0; i < TYPE_STOPS.length; i++) {
      const start = TYPE_STOPS[i] - TYPE_LEAD;
      const end = TYPE_STOPS[i] - TYPE_GAP;
      if (p < start || p > end) continue;
      const q = chat[(i + 1) % chat.length].q;
      const done = Math.min(1, (p - start) / (end - start - 550));
      return q.slice(0, Math.round(q.length * done));
    }
    return "";
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <style>{`
        .svd-ion svg { width: 1em; height: 1em; fill: currentColor; }
        .svd-ion .ionicon-fill-none { fill: none; stroke: currentColor; }
        .svd-ion .ionicon-stroke-width { stroke-width: 32px; }
        /* the design system's own button interaction ramp */
        .sv-hero-cta:hover { filter: brightness(0.94); }
        .sv-hero-cta:active { transform: scale(0.97); filter: brightness(0.9); }
        .sv-hero-cta:focus-visible { outline: 2px solid #F97316; outline-offset: 2px; }
        @keyframes svRise {
          0%   { opacity: 0; transform: translateY(140px) rotate(3.5deg) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
        }
        @keyframes svWake { 0%, 8% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes svPreIn {
          0%   { opacity: 0; transform: translateY(26px); filter: blur(6px); }
          18%  { opacity: 1; transform: translateY(0);    filter: blur(0); }
          72%  { opacity: 1; transform: translateY(0);    filter: blur(0); }
          100% { opacity: 0; transform: translateY(-30px); filter: blur(5px); }
        }
        @keyframes svUp { 0% { opacity: 0; transform: translateY(18px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes svFade { 0% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes svRule { 0% { transform: scaleX(0); } 100% { transform: scaleX(1); } }
        @keyframes svTradeIn {
          0%   { opacity: 0; transform: translateY(16px); filter: blur(4px); }
          100% { opacity: 1; transform: translateY(0);    filter: blur(0); }
        }
        @keyframes svEject {
          0%   { opacity: 0; transform: translate(var(--bx,0px), var(--by,0px)) scale(0.42);
                 background-color: #FFFFFF; border-color: #DCE3DD;
                 box-shadow: 0 18px 40px -20px rgba(16,33,27,0.45); }
          14%  { opacity: 1; }
          62%  { background-color: #FFFFFF; border-color: #DCE3DD;
                 box-shadow: 0 18px 40px -20px rgba(16,33,27,0.45); }
          100% { opacity: 1; transform: translate(0,0) scale(1);
                 background-color: rgba(255,255,255,0); border-color: rgba(220,227,221,0);
                 box-shadow: 0 0 0 rgba(16,33,27,0); }
        }
        @keyframes svChat {
          0%, 20%   { transform: translateY(0); }
          24%, 45%  { transform: translateY(-946px); }
          49%, 70%  { transform: translateY(-1892px); }
          74%, 100% { transform: translateY(-2838px); }
        }
        @keyframes svGlow {
          0%, 100% { opacity: 0.5;  transform: translateX(-50%) scale(1); }
          50%      { opacity: 0.85; transform: translateX(-50%) scale(1.06); }
        }
        @keyframes svCaret { 0%, 45% { opacity: 1; } 50%, 95% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes svBeamGrow { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }
        @keyframes svBeamDot {
          0%   { opacity: 0; transform: translate(-50%, 0); }
          12%  { opacity: 1; }
          78%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, 150px); }
        }
        @keyframes svCueBob {
          0%, 100% { transform: translateY(0); opacity: 0.75; }
          50%      { transform: translateY(5px); opacity: 1; }
        }
        /* Tablet and below: the old hero hid the media column; keep that. */
        @media (max-width: 1040px) {
          .sv-hero-stage { display: none !important; }
          .sv-hero-text  { max-width: 100% !important; }
          .sv-hero-beam  { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sv-hero *, .sv-hero *::before, .sv-hero *::after {
            animation-duration: 0.001s !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>

      <section
        className="sv-hero hero"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          minHeight: "min(880px, calc(100vh - 64px))",
          padding: "56px clamp(20px, 6vw, 64px) 0",
          overflow: "hidden",
          background: "#FFFFFF",
          color: "#10211B",
        }}
      >
        {/* ambient light behind the phone */}
        <div style={{ position: "absolute", left: "62%", bottom: -140, width: 900, height: 900, transform: "translateX(-50%)", borderRadius: "50%", background: "radial-gradient(closest-side, rgba(224,236,231,0.85), rgba(224,236,231,0))", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: "62%", bottom: -60, width: 460, height: 300, transform: "translateX(-50%)", borderRadius: "50%", background: "radial-gradient(closest-side, rgba(249,115,22,0.16), rgba(249,115,22,0))", filter: "blur(6px)", pointerEvents: "none", animation: `svGlow 6s ${EASE} 1.4s infinite` }} />
        {/* hero dissolves into the next section's sage canvas */}
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 200, zIndex: 3, background: "linear-gradient(180deg, rgba(243,245,241,0), #F3F5F1)", pointerEvents: "none" }} />

        {/* ---------------------------------------------------------- copy -- */}
        <div className="sv-hero-text hero-text" style={{ position: "relative", zIndex: 5, maxWidth: 468, flex: "0 0 auto", minWidth: 0, paddingBottom: 64, paddingTop: 80 }}>
          {/* the pre-headline resolves into this eyebrow */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, height: 22, marginBottom: 22, animation: `svFade 0.7s ${EASE} 2.05s both` }}>
            <span style={{ display: "block", width: 34, height: 1, background: "#C7D2CC", transformOrigin: "left", animation: `svRule 0.6s ${EASE} 2.1s both` }} />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "#C2410C", whiteSpace: "nowrap" }}>{t.eyebrow}</span>
            <span style={{ display: "block", flex: 1, height: 1, background: "#E7ECE6", transformOrigin: "left", animation: `svRule 0.8s ${EASE} 2.3s both` }} />
          </div>

          <div style={{ position: "relative", display: "flex", alignItems: "flex-end", minHeight: 172, marginBottom: 22 }}>
            <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, pointerEvents: "none", animation: `svPreIn 2.9s ${EASE} 0.35s both` }}>
              <div style={{ fontFamily: "'Newsreader', serif", fontSize: "clamp(34px, 3.4vw, 50px)", lineHeight: 1.04, letterSpacing: "-0.025em", fontWeight: 600, color: "#10211B" }}>
                {t.preHead[0]} <em style={{ fontStyle: "italic", color: "#2F6B48" }}>{t.preHead[1]}</em>
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#597066", marginTop: 14 }}>{t.preSub}</div>
            </div>

            <h1 style={{ fontFamily: "'Sora', 'DM Sans', sans-serif", fontSize: "clamp(30px, 3vw, 48px)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.03em", margin: 0, animation: `svUp 0.9s ${EASE} 2.45s both` }}>
              {t.h1}
              <span ref={tradeRef} style={{ display: "block", color: "#1C5138", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minHeight: "1.05em" }}>
                {t.trades[trade]}
              </span>
            </h1>
          </div>

          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, lineHeight: 1.55, color: "#597066", margin: "0 0 30px", maxWidth: 430, animation: `svUp 0.9s ${EASE} 2.75s both` }}>
            {t.sub}
          </p>

          {/* these rows fly out of the phone screen */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 34 }}>
            {t.bullets.map((b, i) => (
              <div
                key={i}
                data-fly={i}
                style={{
                  position: "relative", display: "flex", alignItems: "flex-start", gap: 13,
                  fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1.45, color: "#10211B",
                  padding: "10px 14px", marginLeft: -14, borderRadius: 14,
                  border: "1px solid rgba(220,227,221,0)", backgroundColor: "rgba(255,255,255,0)",
                  willChange: "transform",
                }}
              >
                <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: "50%", background: BULLET_STYLE[i].tint, display: "flex", alignItems: "center", justifyContent: "center", color: BULLET_STYLE[i].ink }}>
                  <Icon name={BULLET_STYLE[i].icon} size={16} />
                </span>
                <span style={{ paddingTop: 4 }}>
                  <Link
                    href={b.href}
                    style={{ fontWeight: 700, color: "inherit", textDecoration: "none", borderBottom: "1px solid rgba(28,81,56,0.28)" }}
                  >
                    {b.bold}
                  </Link>{" "}
                  <span style={{ color: "#597066" }}>{b.text}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="hero-cta-row" style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", animation: `svUp 0.9s ${EASE} 5.9s both` }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
              <button
                className="sv-hero-cta"
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                  fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600,
                  padding: "14px 24px", borderRadius: 12, border: "1px solid transparent",
                  cursor: "pointer", whiteSpace: "nowrap", color: "#FFFFFF",
                  background: "#F97316", boxShadow: "0 8px 20px rgba(194,65,12,0.28)",
                  transition: `filter 0.16s ${EASE}, transform 0.16s ${EASE}`,
                }}
              >
                {t.cta}
              </button>
            </SignUpButton>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: "#597066" }}>{t.ctaNote}</span>
          </div>

          <Link href={t.cueHref} style={{ display: "inline-flex", alignItems: "center", gap: 9, marginTop: 40, fontFamily: "'DM Mono', monospace", fontSize: 10.5, fontWeight: 500, letterSpacing: "0.16em", textTransform: "uppercase", color: "#597066", textDecoration: "none", animation: "svFade 1s ease 6.6s both" }}>
            <span style={{ display: "inline-flex", animation: "svCueBob 2.4s ease-in-out 7.2s infinite" }}>
              <span style={{ display: "inline-flex", transform: "rotate(90deg)" }}>
                <Icon name="arrow-forward" size={13} />
              </span>
            </span>
            {t.cue}
          </Link>
        </div>

        {/* ------------------------------------------------- hand + phone -- */}
        <div ref={stageRef} className="sv-hero-stage hero-dashboard" style={{ position: "relative", zIndex: 4, flex: "1 1 0", minWidth: 0, alignSelf: "stretch" }}>
          <div ref={groupRef} style={{ position: "absolute", left: "50%", bottom: -30, marginLeft: -ART_W / 2, width: ART_W, height: ART_H, transformOrigin: "50% 100%", willChange: "transform", WebkitMaskImage: WRIST_FADE, maskImage: WRIST_FADE }}>
            <div style={{ position: "absolute", inset: 0, transformOrigin: "50% 100%", animation: `svRise 1.5s ${EASE} 0.1s both`, willChange: "transform" }}>
              {/* live UI, sitting behind the PNG's transparent screen cut-out,
                  so the notch and the fingers overlap it for free */}
              <div
                ref={screenRef}
                style={{
                  position: "absolute", left: SCREEN.left, top: SCREEN.top,
                  width: SCREEN.width, height: SCREEN.height, borderRadius: SCREEN.radius,
                  overflow: "hidden", background: "#10211B",
                }}
              >
                <div style={{ position: "absolute", left: 0, top: 0, width: UI.width, height: UI.height, transform: `scale(${UI.scale})`, transformOrigin: "0 0" }}>
                <div style={{ position: "absolute", inset: 0, background: "#F3F5F1", display: "flex", flexDirection: "column", animation: `svWake 1.4s ${EASE} 0.85s both` }}>
                  <div style={{ height: 58, flexShrink: 0, display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: "0 24px 4px" }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 600 }}>9.41</span>
                    <span style={{ display: "flex", alignItems: "flex-end", gap: 7 }}>
                      <span style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                        {[5, 8, 11, 14].map((h) => (
                          <span key={h} style={{ display: "block", width: 3, height: h, borderRadius: 1, background: "currentColor" }} />
                        ))}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span style={{ display: "block", width: 24, height: 13, border: "1.5px solid currentColor", borderRadius: 4, padding: 2 }}>
                          <span style={{ display: "block", width: "100%", height: "100%", borderRadius: 2, background: "currentColor" }} />
                        </span>
                        <span style={{ display: "block", width: 2, height: 5, borderRadius: "0 2px 2px 0", background: "currentColor" }} />
                      </span>
                    </span>
                  </div>

                  <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "14px 22px 16px", background: "#FFFFFF", borderBottom: "1px solid #E7ECE6" }}>
                    <span style={{ width: 34, height: 34, borderRadius: "50%", background: "#FFEFDD", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                      <Image src="/logo.png" alt="" width={46} height={46} style={{ width: 23, height: 23, objectFit: "contain", display: "block" }} />
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontFamily: "'Newsreader', serif", fontSize: 22, fontWeight: 600, lineHeight: 1 }}>Svend</span>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#355247" }}>{t.assistant}</span>
                    </span>
                    <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, color: "#16312A", background: "#E0ECE7", padding: "5px 10px", borderRadius: 8, lineHeight: 1 }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#2F6B48" }} />
                      {t.online}
                    </span>
                  </div>

                  <div style={{ flex: "1 1 0", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", left: 0, right: 0, top: 0, animation: `svChat 15s ${EASE} 1.6s infinite`, willChange: "transform" }}>
                      {chat.map((c, i) => (
                        <div key={i} style={{ height: 946, padding: "22px 24px 18px", display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 13 }}>
                          <div style={{ alignSelf: "flex-end", maxWidth: 320, background: "#10211B", color: "#EEF4F0", fontFamily: "'DM Sans', sans-serif", fontSize: 17.5, fontWeight: 500, lineHeight: 1.4, padding: "13px 16px", borderRadius: "16px 16px 5px 16px" }}>{c.q}</div>
                          <div style={{ alignSelf: "flex-start", maxWidth: 310, background: "#FFFFFF", border: "1px solid #E7ECE6", color: "#10211B", fontFamily: "'DM Sans', sans-serif", fontSize: 17.5, lineHeight: 1.4, padding: "13px 16px", borderRadius: "16px 16px 16px 5px" }}>{c.a}</div>
                          <div style={{ alignSelf: "stretch", background: "#FFFFFF", border: "1px solid #DCE3DD", borderRadius: 16, padding: "14px 16px", boxShadow: "0 1px 2px rgba(16,33,27,0.04), 0 6px 16px rgba(16,33,27,0.06)" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#355247" }}>{c.label}</span>
                              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, color: "#16312A", background: "#E0ECE7", padding: "5px 10px", borderRadius: 8, lineHeight: 1 }}>{c.chip}</span>
                            </div>
                            <div style={{ fontFamily: "'Newsreader', serif", fontSize: 32, fontWeight: 600, lineHeight: 1 }}>{c.value}</div>
                            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: "#355247", marginTop: 7 }}>{c.meta}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: 26, background: "linear-gradient(180deg, #F3F5F1, rgba(243,245,241,0))", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 12, background: "linear-gradient(0deg, #F3F5F1, rgba(243,245,241,0))", pointerEvents: "none" }} />
                  </div>

                  <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 11, padding: "13px 18px 22px", background: "#FFFFFF", borderTop: "1px solid #E7ECE6" }}>
                    <span style={{ position: "relative", flex: 1, display: "flex", alignItems: "center", minWidth: 0, height: 46, padding: "0 16px", border: "1px solid #C7D2CC", borderRadius: 999, overflow: "hidden", fontFamily: "'DM Sans', sans-serif", fontSize: 16.5, color: "#10211B" }}>
                      <span ref={phRef} style={{ position: "absolute", left: 16, color: "#355247" }}>{t.input}</span>
                      <span ref={typeRef} style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden" }} />
                      <span ref={caretRef} style={{ display: "block", flexShrink: 0, width: 1.5, height: 17, marginLeft: 2, background: "#C2410C", opacity: 0, animation: "svCaret 1.1s steps(1) infinite" }} />
                    </span>
                    <span style={{ flexShrink: 0, width: 44, height: 44, borderRadius: "50%", background: "#C2410C", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 18px -8px rgba(194,65,12,0.7)" }}>
                      <Icon name="arrow-up" size={21} />
                    </span>
                  </div>
                </div>
                </div>
              </div>

              <Image
                src="/hand-phone.png"
                alt={t.alt}
                width={ART_W}
                height={ART_H}
                priority
                sizes="(max-width: 1040px) 0px, 660px"
                style={{ position: "absolute", left: 0, top: 0, width: ART_W, height: ART_H, display: "block", pointerEvents: "none", filter: "drop-shadow(0 26px 44px rgba(16,33,27,0.16))" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* hairline that carries the eye from the phone into the next section.
          Lives outside the hero so it isn't clipped; JS aligns it to the screen. */}
      <div
        ref={beamRef}
        className="sv-hero-beam"
        aria-hidden="true"
        style={{
          position: "absolute", bottom: -172, width: 1, height: 168,
          transform: "translateX(-50%)", transformOrigin: "top",
          background: "linear-gradient(180deg, rgba(199,210,204,0), #C7D2CC 30%, rgba(199,210,204,0))",
          animation: `svBeamGrow 1.2s ${EASE} 6.2s both`, pointerEvents: "none", zIndex: 4,
        }}
      >
        <span style={{ position: "absolute", left: "50%", top: 0, width: 7, height: 7, borderRadius: "50%", background: "#C2410C", boxShadow: "0 0 12px rgba(194,65,12,0.8)", animation: "svBeamDot 3.6s cubic-bezier(0.4,0,0.6,1) 7.2s infinite" }} />
      </div>
    </div>
  );
}
